import { Routes, Route, Navigate } from "react-router-dom";
import PageNotFound from "./components/PageNotFound";
import Dashboard from "./components/dashboard/Dashboard";
import Layout from "./components/Layout";
import ProtectedRoutes from "./components/ProtectedRoutes";
import Login from "./components/authentication/Login";
import Register from "./components/authentication/Register";
import ForgotPassword from "./components/authentication/ForgotPassword";
import SubscriptionPlan from "./components/SubscriptionPlan";
import CustomLead from "./components/CustomLead";
import GenerateReports from "./components/generate-reports/GenerateReports";
import PastReports from "./components/past-reports/PastReports";
import Plans from "./components/plans/Plans";
import AdminPlanManagement from "./components/admin-plan-management/AdminPlanManagement";
import AddWhiteLabels from "./components/add-white-labels/AddWhiteLabels";
import ViewGeneratedReport from "./components/generate-reports/ViewGeneratedReport";
import UserProfile from "./components/profile/UserProfile";
import TrackLeadsDashboard from "./components/track-your-leads/TrackLeadsDashboard";
import EstimatorHook from "./components/estimator-hook/EstimatorHook";
import Subscription from "./components/subscriptions/Subscription";
import UserManagement from "./components/user-management/UserManagement";
import EmbeddedEstimator from "./components/estimator-hook/EmbeddedEstimator";
import ViewPlan from "./components/subscriptions/ViewPlan";
import EditPlan from "./components/subscriptions/EditPlan";
import ViewUser from "./components/user-management/ViewUser";
import EditUser from "./components/user-management/EditUser";
import TermsAndConditions from "./components/authentication/TermsAndConditions";
import EmbeddedReports from "./components/embed-reports/EmbeddedReports";
import AdminDashboard from "./components/admin-dashboard/AdminDashboard";
import FleetReports from "./components/fleet-reports/FleetReports";
import ViewFleetReport from "./components/fleet-reports/ViewFleetReport";
import { ContactUs } from "./components/contact-us/ContactUs";
import ThankYou from "./components/estimator-hook/ThankYou";
const AppRoutes = () => {

  // Retrieve user role from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = userData?.role || "customer";

  // Determine default route based on user role
  const defaultRoute =
    userRole === "admin" ? "/main/admin-dashboard" : "/main/dashboard";


  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms-conditions" element={<TermsAndConditions />} />
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Public embedded estimator route (no auth required for iframe embedding) */}
      <Route path="/embed/:clientId" element={<EmbeddedEstimator />} />

      {/* Protected subscription plan route (outside /main) */}
      <Route
        path="/subscription-plan"
        element={
          <ProtectedRoutes allowedRoles={["customer"]}>
            <SubscriptionPlan />
          </ProtectedRoutes>
        }
      />

      {/* Protected custom lead route (outside /main) */}
      <Route
        path="/custom-lead"
        element={
          <ProtectedRoutes allowedRoles={["customer"]}>
            <CustomLead />
          </ProtectedRoutes>
        }
      />

      {/* Protected routes for the dashboard */}
      <Route
        path="/main"
        element={
          <ProtectedRoutes allowedRoles={["admin", "customer"]}>
            <Layout />
          </ProtectedRoutes>
        }
      >
        {/* Default route based on user role */}
        <Route index element={<Navigate to={defaultRoute} replace />} />

        {/* Dashboard route only for customers */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <Dashboard />
            </ProtectedRoutes>
          }
        />

        {/* Dashboard route only for admin */}
        <Route
          path="admin-dashboard"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoutes>
          }
        />

        {/* Track Your Leads */}
        <Route
          path="track-your-leads"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <TrackLeadsDashboard />
            </ProtectedRoutes>
          }
        />

        {/* Subscriptions */}
        <Route
          path="subscriptions"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <Subscription />
            </ProtectedRoutes>
          }
        />

        <Route
          path="subscriptions/create-plan"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <AdminPlanManagement />
            </ProtectedRoutes>
          }
        />

        {/* View Plan */}
        <Route
          path="subscriptions/view-plan/:planId"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <ViewPlan />
            </ProtectedRoutes>
          }
        />

        {/* Edit Plan */}
        <Route
          path="subscriptions/edit-plan/:planId"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <EditPlan />
            </ProtectedRoutes>
          }
        />

        {/* User Management */}
        <Route
          path="user-management"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <UserManagement />
            </ProtectedRoutes>
          }
        />

        {/* View User */}
        <Route
          path="user-management/view-user/:userId"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <ViewUser />
            </ProtectedRoutes>
          }
        />

        {/* Edit User */}
        <Route
          path="user-management/edit-user/:userId"
          element={
            <ProtectedRoutes allowedRoles={["admin"]}>
              <EditUser />
            </ProtectedRoutes>
          }
        />

        {/* Generate Reports */}
        <Route
          path="generate-reports"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <GenerateReports />
            </ProtectedRoutes>
          }
        />

        {/* View Generated Report */}
        <Route
          path="generate-reports/view"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <ViewGeneratedReport />
            </ProtectedRoutes>
          }
        />

        {/* Fleet Estimator */}
        <Route
          path="fleet-reports"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <FleetReports />
            </ProtectedRoutes>
          }
        />

        {/* View Fleet Estimator Report */}
        <Route
          path="fleet-reports/view"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <ViewFleetReport />
            </ProtectedRoutes>
          }
        />

        {/* Estimator Hook */}
        <Route
          path="estimator-hook"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <EstimatorHook />
            </ProtectedRoutes>
          }
        />

        {/* Add White Labels */}
        <Route
          path="add-white-labels"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <AddWhiteLabels />
            </ProtectedRoutes>
          }
        />

        {/* User Profile */}
        <Route
          path="user-profile"
          element={
            <ProtectedRoutes allowedRoles={["admin", "customer"]}>
              <UserProfile />
            </ProtectedRoutes>
          }
        />

        {/* Past Reports */}
        <Route
          path="past-reports"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <PastReports />
            </ProtectedRoutes>
          }
        />

        {/* Embed Reports */}
        <Route
          path="embed-reports"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <EmbeddedReports />
            </ProtectedRoutes>
          }
        />

        {/* Plans */}
        <Route
          path="plans"
          element={
            <ProtectedRoutes allowedRoles={["customer"]}>
              <Plans />
            </ProtectedRoutes>
          }
        />
<Route path="/main/generate-reports/thank-you" element={<ThankYou />} />

        {/* Contact Us */}
        <Route
          path="contact-us"
          element={
            <ProtectedRoutes allowedRoles={["admin", "customer"]}>
              <ContactUs />
            </ProtectedRoutes>
          }
        />
      </Route>

      {/* Catch-all route for unknown paths */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRoutes;
